import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { adminAPI } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import {
  Search,
  Plus,
  Mail,
  Trash2,
  Edit,
  Building2,
  UserPlus,
  Users,
  Phone,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function AdminWhitelist() {
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [companyUsers, setCompanyUsers] = useState({});
  const [loadingUsers, setLoadingUsers] = useState({});
  const [userFormData, setUserFormData] = useState({
    email: '',
    phone: '',
    companyRole: 'member'
  });
  const [formData, setFormData] = useState({
    companyName: '',
    pocFirstName: '',
    pocLastName: '',
    pocEmail: '',
    pocCountryCode: '+91',
    pocPhone: '',
    pocDesignation: '',
    role: 'company',
    demoType: 'startup',
    goldPassQuota: 2,
    silverPlusPassQuota: 0,
    silverPassQuota: 2
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchWhitelist();
  }, []);

  const fetchWhitelist = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getWhitelist();
      setWhitelist(response.data.whitelist || []);
    } catch (error) {
      console.error('Error fetching whitelist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWhitelist = async () => {
    // Validate required fields
    if (!formData.companyName || !formData.pocFirstName || !formData.pocLastName ||
        !formData.pocEmail || !formData.pocPhone || !formData.pocDesignation) {
      toast({
        title: 'Error',
        description: 'All fields are required',
        variant: 'destructive',
      });
      return;
    }

    // Validate demoType if role is 'company'
    if (formData.role === 'company' && !formData.demoType) {
      toast({
        title: 'Error',
        description: 'Demo type is required for demo companies',
        variant: 'destructive',
      });
      return;
    }

    try {
      const requestData = {
        companyName: formData.companyName,
        pocFirstName: formData.pocFirstName,
        pocLastName: formData.pocLastName,
        pocEmail: formData.pocEmail,
        pocCountryCode: formData.pocCountryCode,
        pocPhone: formData.pocPhone,
        pocDesignation: formData.pocDesignation,
        role: formData.role,
        goldPassQuota: formData.goldPassQuota,
        silverPlusPassQuota: formData.silverPlusPassQuota,
        silverPassQuota: formData.silverPassQuota
      };

      // Include demoType only if role is 'company'
      if (formData.role === 'company') {
        requestData.demoType = formData.demoType;
      }

      await adminAPI.addToWhitelist(requestData);

      toast({
        title: 'Success',
        description: 'Company registered successfully',
      });

      setShowAddModal(false);
      setFormData({
        companyName: '',
        pocFirstName: '',
        pocLastName: '',
        pocEmail: '',
        pocCountryCode: '+91',
        pocPhone: '',
        pocDesignation: '',
        role: 'company',
        demoType: 'startup',
        goldPassQuota: 2,
        silverPlusPassQuota: 0,
        silverPassQuota: 2
      });
      fetchWhitelist();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to register company',
        variant: 'destructive',
      });
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setFormData({
      companyName: company.companyName || '',
      pocFirstName: company.pocFirstName || company.pocName?.split(' ')[0] || '',
      pocLastName: company.pocLastName || company.pocName?.split(' ').slice(1).join(' ') || '',
      pocEmail: company.email || company.pocEmail || '',
      pocCountryCode: company.pocCountryCode || '+91',
      pocPhone: company.pocPhone || '',
      pocDesignation: company.pocDesignation || '',
      role: company.role || 'company',
      demoType: company.demoType || 'startup',
      goldPassQuota: company.goldPassQuota !== undefined ? company.goldPassQuota : 2,
      silverPlusPassQuota: company.silverPlusPassQuota !== undefined ? company.silverPlusPassQuota : 0,
      silverPassQuota: company.silverPassQuota !== undefined ? company.silverPassQuota : 2
    });
    setShowEditModal(true);
  };

  const handleUpdateCompany = async () => {
    // Validate required fields (pocDesignation is optional for backward compatibility)
    if (!formData.companyName || !formData.pocFirstName || !formData.pocLastName ||
        !formData.pocEmail || !formData.pocPhone) {
      toast({
        title: 'Error',
        description: 'Company name, POC name, email, and phone are required',
        variant: 'destructive',
      });
      return;
    }

    // Validate demoType if role is 'company'
    if (formData.role === 'company' && !formData.demoType) {
      toast({
        title: 'Error',
        description: 'Demo type is required for demo companies',
        variant: 'destructive',
      });
      return;
    }

    try {
      const updateData = {
        companyName: formData.companyName,
        pocFirstName: formData.pocFirstName,
        pocLastName: formData.pocLastName,
        pocEmail: formData.pocEmail,
        pocCountryCode: formData.pocCountryCode,
        pocPhone: formData.pocPhone,
        pocDesignation: formData.pocDesignation,
        role: formData.role,
        goldPassQuota: formData.goldPassQuota,
        silverPlusPassQuota: formData.silverPlusPassQuota,
        silverPassQuota: formData.silverPassQuota
      };

      // Include demoType only if role is 'company'
      if (formData.role === 'company') {
        updateData.demoType = formData.demoType;
      }

      await adminAPI.updateCompany(editingCompany._id, updateData);

      toast({
        title: 'Success',
        description: 'Company updated successfully',
      });

      setShowEditModal(false);
      setEditingCompany(null);
      setFormData({
        companyName: '',
        pocFirstName: '',
        pocLastName: '',
        pocEmail: '',
        pocCountryCode: '+91',
        pocPhone: '',
        pocDesignation: '',
        role: 'company',
        demoType: 'startup',
        goldPassQuota: 2,
        silverPlusPassQuota: 0,
        silverPassQuota: 2
      });
      fetchWhitelist();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update company',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteCompany = async (company) => {
    if (window.confirm(`Are you sure you want to delete ${company.companyName}? This will remove the company and all associated data.`)) {
      try {
        await adminAPI.deleteCompany(company._id);
        toast({
          title: 'Success',
          description: 'Company deleted successfully',
        });
        fetchWhitelist();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete company',
          variant: 'destructive',
        });
      }
    }
  };

  const toggleCompanyUsers = async (companyId) => {
    if (expandedCompanyId === companyId) {
      setExpandedCompanyId(null);
    } else {
      setExpandedCompanyId(companyId);

      // Load users if not already loaded
      if (!companyUsers[companyId]) {
        setLoadingUsers({ ...loadingUsers, [companyId]: true });
        try {
          const response = await adminAPI.getCompanyUsers(companyId);
          setCompanyUsers({ ...companyUsers, [companyId]: response.data.data });
        } catch (error) {
          console.error('Error loading users:', error);
          toast({
            title: 'Error',
            description: 'Failed to load company users',
            variant: 'destructive',
          });
        } finally {
          setLoadingUsers({ ...loadingUsers, [companyId]: false });
        }
      }
    }
  };

  const handleRemoveUser = async (companyId, userId, userEmail) => {
    if (window.confirm(`Are you sure you want to remove ${userEmail || 'this user'} from the company?`)) {
      try {
        await adminAPI.removeUserFromCompany(companyId, userId);

        toast({
          title: 'Success',
          description: 'User removed from company successfully',
        });

        // Refresh the users list
        const response = await adminAPI.getCompanyUsers(companyId);
        setCompanyUsers({ ...companyUsers, [companyId]: response.data.data });
      } catch (error) {
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to remove user',
          variant: 'destructive',
        });
      }
    }
  };

  const handleUpdateUserRole = async (companyId, userId, newRole) => {
    try {
      await adminAPI.updateUserCompanyRole(companyId, userId, { companyRole: newRole });

      toast({
        title: 'Success',
        description: 'User role updated successfully',
      });

      // Refresh the users list
      const response = await adminAPI.getCompanyUsers(companyId);
      setCompanyUsers({ ...companyUsers, [companyId]: response.data.data });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update user role',
        variant: 'destructive',
      });
    }
  };

  const handleAddUserToCompany = async () => {
    // Validate that at least email or phone is provided
    if (!userFormData.email && !userFormData.phone) {
      toast({
        title: 'Error',
        description: 'Please provide either email or phone number',
        variant: 'destructive',
      });
      return;
    }

    try {
      await adminAPI.addUserToCompany({
        companyId: selectedCompany._id,
        email: userFormData.email || undefined,
        phone: userFormData.phone || undefined,
        companyRole: userFormData.companyRole
      });

      toast({
        title: 'Success',
        description: `User added to ${selectedCompany.companyName} successfully`,
      });

      setShowAddUserModal(false);
      setUserFormData({ email: '', phone: '', companyRole: 'member' });

      // Refresh users list if this company is expanded
      if (expandedCompanyId === selectedCompany._id) {
        const response = await adminAPI.getCompanyUsers(selectedCompany._id);
        setCompanyUsers({ ...companyUsers, [selectedCompany._id]: response.data.data });
      }

      setSelectedCompany(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add user to company',
        variant: 'destructive',
      });
    }
  };

  const filteredWhitelist = whitelist.filter(entry =>
    entry.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Company Registration</CardTitle>
              <CardDescription>Register companies and manage authorized access</CardDescription>
            </div>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Register Company
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by company name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredWhitelist.map((entry) => (
                <Card key={entry._id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <Building2 className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-base">{entry.companyName || 'Unknown Company'}</p>
                            <Badge
                              variant={
                                entry.role === 'admin' ? 'destructive' :
                                entry.role === 'sponsor' ? 'success' :
                                entry.role === 'non-demo' ? 'secondary' :
                                'default'
                              }
                              className="text-xs"
                            >
                              {entry.role === 'company' ? 'Demo' : entry.role}
                            </Badge>
                          </div>
                          <div className="mt-1 space-y-1">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">POC:</span> {entry.pocName || 'Not set'}
                              {entry.pocDesignation && (
                                <span className="text-muted-foreground"> • {entry.pocDesignation}</span>
                              )}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {entry.email}
                              </span>
                              {entry.pocPhone && (
                                <span>{entry.pocPhone}</span>
                              )}
                            </div>
                            {entry.role === 'company' && entry.demoType && (
                              <p className="text-xs text-muted-foreground">
                                Demo Type: <span className="font-medium capitalize">{entry.demoType}</span>
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Registered by {entry.addedBy} • {new Date(entry.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCompanyUsers(entry._id)}
                          title="View company users"
                          className="h-8 w-8 p-0"
                        >
                          {expandedCompanyId === entry._id ?
                            <ChevronUp className="h-4 w-4" /> :
                            <Users className="h-4 w-4" />
                          }
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedCompany(entry);
                            setShowAddUserModal(true);
                            setUserFormData({ email: '', phone: '', companyRole: 'member' });
                          }}
                          title="Add user to company"
                          className="h-8 w-8 p-0"
                        >
                          <UserPlus className="h-4 w-4 text-primary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCompany(entry)}
                          title="Edit company"
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCompany(entry)}
                          title="Delete company"
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Users Section */}
                    {expandedCompanyId === entry._id && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Company Users
                        </h4>

                        {loadingUsers[entry._id] ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                          </div>
                        ) : companyUsers[entry._id] && companyUsers[entry._id].length > 0 ? (
                          <div className="space-y-2">
                            {companyUsers[entry._id].map((user) => {
                              const isPOC = user.email === entry.pocEmail || user.email === entry.email;
                              return (
                                <div key={user._id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                      <div>
                                        {user.email && (
                                          <p className="text-sm flex items-center gap-1">
                                            <Mail className="h-3 w-3 text-muted-foreground" />
                                            {user.email}
                                            {isPOC && <span className="text-xs text-primary ml-1">(POC)</span>}
                                          </p>
                                        )}
                                        {user.phone && (
                                          <p className="text-sm flex items-center gap-1">
                                            <Phone className="h-3 w-3 text-muted-foreground" />
                                            {user.phone}
                                          </p>
                                        )}
                                      </div>
                                      <select
                                        className="text-xs px-2 py-1 border rounded"
                                        value={user.companyRole || 'member'}
                                        onChange={(e) => handleUpdateUserRole(entry._id, user._id, e.target.value)}
                                        disabled={isPOC}
                                        title={isPOC ? "Cannot change POC role. Update company details instead." : "Change user role"}
                                      >
                                        <option value="owner">Owner</option>
                                        <option value="admin">Admin</option>
                                        <option value="member">Member</option>
                                        <option value="viewer">Viewer</option>
                                      </select>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Joined: {user.joinedCompanyAt ? new Date(user.joinedCompanyAt).toLocaleDateString() : 'N/A'}
                                    </p>
                                  </div>
                                  {!isPOC && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRemoveUser(entry._id, user._id, user.email || user.phone)}
                                      className="h-8 w-8 p-0"
                                      title="Remove user from company"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground py-2">
                            No users added yet. POC can login with their registered email/phone.
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Company Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Register New Company</DialogTitle>
            <DialogDescription>
              Add a new company to the platform with POC details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Company Details */}
            <div>
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={(e) => setFormData({...formData, companyName: e.target.value})}
              />
            </div>

            {/* POC Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pocFirstName">POC First Name *</Label>
                <Input
                  id="pocFirstName"
                  placeholder="First name"
                  value={formData.pocFirstName}
                  onChange={(e) => setFormData({...formData, pocFirstName: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="pocLastName">POC Last Name *</Label>
                <Input
                  id="pocLastName"
                  placeholder="Last name"
                  value={formData.pocLastName}
                  onChange={(e) => setFormData({...formData, pocLastName: e.target.value})}
                />
              </div>
            </div>

            {/* POC Email */}
            <div>
              <Label htmlFor="pocEmail">POC Email *</Label>
              <Input
                id="pocEmail"
                type="email"
                placeholder="poc@company.com"
                value={formData.pocEmail}
                onChange={(e) => setFormData({...formData, pocEmail: e.target.value})}
              />
            </div>

            {/* POC Phone */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="pocCountryCode">Country Code</Label>
                <Input
                  id="pocCountryCode"
                  placeholder="+91"
                  value={formData.pocCountryCode}
                  onChange={(e) => setFormData({...formData, pocCountryCode: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="pocPhone">POC Phone Number *</Label>
                <Input
                  id="pocPhone"
                  placeholder="9876543210"
                  value={formData.pocPhone}
                  onChange={(e) => setFormData({...formData, pocPhone: e.target.value})}
                />
              </div>
            </div>

            {/* POC Designation */}
            <div>
              <Label htmlFor="pocDesignation">POC Designation *</Label>
              <Input
                id="pocDesignation"
                placeholder="CEO, CTO, Product Manager, etc."
                value={formData.pocDesignation}
                onChange={(e) => setFormData({...formData, pocDesignation: e.target.value})}
              />
            </div>

            {/* Role */}
            <div>
              <Label htmlFor="role">Company Role</Label>
              <select
                id="role"
                className="w-full px-3 py-2 border rounded-md"
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
              >
                <option value="company">Demo Company</option>
                <option value="non-demo">Non-Demo</option>
                <option value="sponsor">Sponsor</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Demo Type (only for Demo Companies) */}
            {formData.role === 'company' && (
              <div>
                <Label htmlFor="demoType">Demo Type *</Label>
                <select
                  id="demoType"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.demoType}
                  onChange={(e) => setFormData({...formData, demoType: e.target.value})}
                >
                  <option value="startup">Startup</option>
                  <option value="scaleup">Scaleup</option>
                </select>
              </div>
            )}

            {/* Pass Quotas */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="goldPassQuota">Gold Pass Quota</Label>
                <Input
                  id="goldPassQuota"
                  type="number"
                  min="0"
                  placeholder="2"
                  value={formData.goldPassQuota}
                  onChange={(e) => setFormData({...formData, goldPassQuota: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label htmlFor="silverPlusPassQuota">Silver Plus Quota</Label>
                <Input
                  id="silverPlusPassQuota"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.silverPlusPassQuota}
                  onChange={(e) => setFormData({...formData, silverPlusPassQuota: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label htmlFor="silverPassQuota">Silver Pass Quota</Label>
                <Input
                  id="silverPassQuota"
                  type="number"
                  min="0"
                  placeholder="2"
                  value={formData.silverPassQuota}
                  onChange={(e) => setFormData({...formData, silverPassQuota: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowAddModal(false);
                setFormData({
                  companyName: '',
                  pocFirstName: '',
                  pocLastName: '',
                  pocEmail: '',
                  pocCountryCode: '+91',
                  pocPhone: '',
                  role: 'company',
                  goldPassQuota: 2,
                  silverPlusPassQuota: 0,
                  silverPassQuota: 2
                });
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddWhitelist}>
                Register Company
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Company Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Company Details</DialogTitle>
            <DialogDescription>
              Update company and POC information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Company Details */}
            <div>
              <Label htmlFor="edit-companyName">Company Name *</Label>
              <Input
                id="edit-companyName"
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={(e) => setFormData({...formData, companyName: e.target.value})}
              />
            </div>

            {/* POC Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-pocFirstName">POC First Name *</Label>
                <Input
                  id="edit-pocFirstName"
                  placeholder="First name"
                  value={formData.pocFirstName}
                  onChange={(e) => setFormData({...formData, pocFirstName: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="edit-pocLastName">POC Last Name *</Label>
                <Input
                  id="edit-pocLastName"
                  placeholder="Last name"
                  value={formData.pocLastName}
                  onChange={(e) => setFormData({...formData, pocLastName: e.target.value})}
                />
              </div>
            </div>

            {/* POC Email */}
            <div>
              <Label htmlFor="edit-pocEmail">POC Email *</Label>
              <Input
                id="edit-pocEmail"
                type="email"
                placeholder="poc@company.com"
                value={formData.pocEmail}
                onChange={(e) => setFormData({...formData, pocEmail: e.target.value})}
              />
            </div>

            {/* POC Phone */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="edit-pocCountryCode">Country Code</Label>
                <Input
                  id="edit-pocCountryCode"
                  placeholder="+91"
                  value={formData.pocCountryCode}
                  onChange={(e) => setFormData({...formData, pocCountryCode: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="edit-pocPhone">POC Phone Number *</Label>
                <Input
                  id="edit-pocPhone"
                  placeholder="9876543210"
                  value={formData.pocPhone}
                  onChange={(e) => setFormData({...formData, pocPhone: e.target.value})}
                />
              </div>
            </div>

            {/* POC Designation */}
            <div>
              <Label htmlFor="edit-pocDesignation">POC Designation *</Label>
              <Input
                id="edit-pocDesignation"
                placeholder="CEO, CTO, Product Manager, etc."
                value={formData.pocDesignation}
                onChange={(e) => setFormData({...formData, pocDesignation: e.target.value})}
              />
            </div>

            {/* Role */}
            <div>
              <Label htmlFor="edit-role">Company Role</Label>
              <select
                id="edit-role"
                className="w-full px-3 py-2 border rounded-md"
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
              >
                <option value="company">Demo Company</option>
                <option value="non-demo">Non-Demo</option>
                <option value="sponsor">Sponsor</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Demo Type (only for Demo Companies) */}
            {formData.role === 'company' && (
              <div>
                <Label htmlFor="edit-demoType">Demo Type *</Label>
                <select
                  id="edit-demoType"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.demoType}
                  onChange={(e) => setFormData({...formData, demoType: e.target.value})}
                >
                  <option value="startup">Startup</option>
                  <option value="scaleup">Scaleup</option>
                </select>
              </div>
            )}

            {/* Pass Quotas */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit-goldPassQuota">Gold Pass Quota</Label>
                <Input
                  id="edit-goldPassQuota"
                  type="number"
                  min="0"
                  placeholder="2"
                  value={formData.goldPassQuota}
                  onChange={(e) => setFormData({...formData, goldPassQuota: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label htmlFor="edit-silverPlusPassQuota">Silver Plus Quota</Label>
                <Input
                  id="edit-silverPlusPassQuota"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.silverPlusPassQuota}
                  onChange={(e) => setFormData({...formData, silverPlusPassQuota: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label htmlFor="edit-silverPassQuota">Silver Pass Quota</Label>
                <Input
                  id="edit-silverPassQuota"
                  type="number"
                  min="0"
                  placeholder="2"
                  value={formData.silverPassQuota}
                  onChange={(e) => setFormData({...formData, silverPassQuota: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowEditModal(false);
                setEditingCompany(null);
                setFormData({
                  companyName: '',
                  pocFirstName: '',
                  pocLastName: '',
                  pocEmail: '',
                  pocCountryCode: '+91',
                  pocPhone: '',
                  role: 'company',
                  goldPassQuota: 2,
                  silverPassQuota: 2
                });
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCompany}>
                Update Company
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User to Company Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add User to {selectedCompany?.companyName}</DialogTitle>
            <DialogDescription>
              Add a new user to this company. They will be able to login immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="user-email">Email Address</Label>
              <Input
                id="user-email"
                type="email"
                placeholder="user@example.com"
                value={userFormData.email}
                onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Provide either email or phone (or both)
              </p>
            </div>

            <div>
              <Label htmlFor="user-phone">Phone Number</Label>
              <Input
                id="user-phone"
                placeholder="9876543210"
                value={userFormData.phone}
                onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="user-role">Company Role</Label>
              <select
                id="user-role"
                className="w-full px-3 py-2 border rounded-md"
                value={userFormData.companyRole}
                onChange={(e) => setUserFormData({...userFormData, companyRole: e.target.value})}
              >
                <option value="owner">Owner</option>
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer (Read-only)</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Owner: Full control | Admin: Can manage data | Member: Can edit | Viewer: Read-only
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowAddUserModal(false);
                setSelectedCompany(null);
                setUserFormData({ email: '', phone: '', companyRole: 'member' });
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddUserToCompany}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}